import { configureStore as configureReduxStore } from '@reduxjs/toolkit';
import promiseMiddleware from 'redux-promise';
import {
  loadingBarReducer,
  showLoading,
  hideLoading,
} from 'react-redux-loading-bar';

import { reducers, actions } from 'partners/actions';

/** @typedef {import('redux').Middleware} Middleware */

// Action loading middleware helpers
const loadingByType = new Set();
const loadingType = (action) => action.type.replace(/\/.*/, '/loading');

function actionLoading(dispatch, action) {
  loadingByType.add(action.type);

  if (action.showLoading !== false) {
    dispatch({
      type: loadingType(action),
      payload: true,
    });

    dispatch(showLoading());
  }
}

function actionUnloading(dispatch, action) {
  loadingByType.delete(action.type);

  setTimeout(() => {
    dispatch({
      type: loadingType(action),
      payload: false,
    });

    dispatch(hideLoading());
  }, 25);
}

/** @type {Middleware} */
export function actionLoadingMiddleware({ dispatch }) {
  return (next) => (action) => {
    if (!action) {
      return;
    }

    // If promise
    if (typeof action.payload?.then === 'function') {
      actionLoading(dispatch, action);
      // Bypass promiseMiddleware
      // This is mainly for API request handling
      // Unlike redux-promise, this will not dispatch a resolved action on error
      // May need to differentiate in case it becomes a problem
      return action.payload
        .then((result) => {
          actionUnloading(dispatch, action);
          dispatch({ ...action, payload: result });
          return result;
        })
        .catch((err) => {
          const message = typeof err === 'string' ? err : String(err.message);

          actionUnloading(dispatch, action);
          dispatch(actions.flash.error(message));

          console.error(err);
        });
    }

    // Continue
    return next(action);
  };
}

function actionLoadingReducer(state = false, action) {
  if (action.type.indexOf('/loading') > 0) {
    if (action.payload) {
      return true;
    }

    if (loadingByType.size <= 0) {
      return false;
    }
  }

  return state;
}

export function configureStore(initialState) {
  return configureReduxStore({
    preloadedState: initialState,
    reducer: {
      ...reducers,
      loadingBar: loadingBarReducer,
      loading: actionLoadingReducer,
    },
    middleware: (getDefaultMiddleware) => [
      ...getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
      }),
      actionLoadingMiddleware,
      promiseMiddleware,
    ],
  });
}
